import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { toastService } from '@/hooks/useToast'
import { Modal } from '@/components/ui/Modal'

// Total inactivity before logout, and how long before that to warn.
const IDLE_LIMIT_MS = 20 * 60 * 1000 // 20 minutes
const WARN_BEFORE_MS = 60 * 1000 // show the warning 1 minute before logout
const WARN_AT_MS = IDLE_LIMIT_MS - WARN_BEFORE_MS // 19 minutes

/**
 * Signs the user out after 20 minutes of inactivity, showing a countdown
 * warning modal for the final minute so they can choose to stay. Any activity
 * (mouse/key/scroll/touch) before the warning resets the timer; once the
 * warning is showing, only clicking "Stay signed in" resets it (so the
 * countdown stays meaningful). Mounted in AppShell → runs only when authed.
 */
export function IdleLogoutGuard() {
  const navigate = useNavigate()
  const [warning, setWarning] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(WARN_BEFORE_MS / 1000)

  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null)
  const warningActive = useRef(false)
  const resetRef = useRef<() => void>(() => {})

  useEffect(() => {
    const clearAllTimers = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current)
      if (logoutTimer.current) clearTimeout(logoutTimer.current)
      if (countdown.current) clearInterval(countdown.current)
    }

    const doLogout = async () => {
      clearAllTimers()
      warningActive.current = false
      setWarning(false)
      const { isAuthenticated, logout } = useAuthStore.getState()
      if (!isAuthenticated) return
      await logout()
      toastService.info('You were signed out after 20 minutes of inactivity.')
      navigate('/login', { replace: true })
    }

    const showWarning = () => {
      warningActive.current = true
      setWarning(true)
      setSecondsLeft(WARN_BEFORE_MS / 1000)
      countdown.current = setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0))
      }, 1000)
      logoutTimer.current = setTimeout(doLogout, WARN_BEFORE_MS)
    }

    const reset = () => {
      clearAllTimers()
      warningActive.current = false
      setWarning(false)
      warnTimer.current = setTimeout(showWarning, WARN_AT_MS)
    }
    resetRef.current = reset

    const onActivity = () => {
      // Once the warning is up, ignore background activity — the user must click
      // "Stay signed in" to reset, so the countdown stays honest.
      if (!warningActive.current) reset()
    }

    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ]
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }))
    reset() // start the countdown

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity))
      clearAllTimers()
    }
  }, [navigate])

  const stay = () => resetRef.current()

  if (!warning) return null
  return (
    <Modal open={warning} title="Are you still there?" onClose={stay} onSave={stay} saveLabel="Stay signed in">
      <div className="text-center py-2">
        <p className="text-sm text-navy dark:text-slate-200">
          You've been inactive for a while. For your security you'll be signed out in
        </p>
        <p className="text-3xl font-bold text-accent my-3" aria-live="polite">
          {secondsLeft}s
        </p>
        <p className="text-xs text-steel dark:text-slate-400">
          Click <span className="font-semibold">Stay signed in</span> to continue your session.
        </p>
      </div>
    </Modal>
  )
}

export default IdleLogoutGuard
